import React, { useState, useRef, useEffect } from "react";
import {
    Button,
    TextField,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Box,
    IconButton,
    Paper,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import SendIcon from "@mui/icons-material/Send";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import axios from "axios";
import jsPDF from "jspdf";
import { KNOWLEDGEX_OCR_URL, KNOWLEDGEX_SUMMARIZE_URL } from "../config/api";
import DocumentChat from "../components/DocumentChat";

const OCR_API_URL = KNOWLEDGEX_OCR_URL;
const SUMMARIZE_API_URL = KNOWLEDGEX_SUMMARIZE_URL;

const extractTextFromOcr = (ocrResult) => {
    if (!ocrResult) return "";
    if (ocrResult.results && Array.isArray(ocrResult.results) && ocrResult.results.length > 0) {
        return ocrResult.results[0].text || "";
    }
    if (typeof ocrResult === "string") return ocrResult;
    if (ocrResult.text) return ocrResult.text;
    if (ocrResult.readResult?.content) return ocrResult.readResult.content;
    if (ocrResult.analyzeResult?.readResults) {
        return ocrResult.analyzeResult.readResults
            .map((page) => page.lines?.map((line) => line.text).join("\n") || "")
            .join("\n\n");
    }
    if (ocrResult.pages) {
        return ocrResult.pages
            .map((page) => page.lines?.map((line) => line.text).join("\n") || "")
            .join("\n\n");
    }
    return "";
};

const DocumentIntelligence = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [ocrResult, setOcrResult] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [cameraOpen, setCameraOpen] = useState(false);
    const [stream, setStream] = useState(null);
    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    // Cleanup camera stream on unmount
    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [stream]);

    const handleFileSelect = (event, isCamera = false) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setOcrResult(null);
            setErrorMessage("");
            
            // Create preview for image files
            if (file.type.startsWith("image/")) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFilePreview(reader.result);
                };
                reader.readAsDataURL(file);
            } else {
                setFilePreview(null);
            }

            // If camera was used, automatically send to OCR
            if (isCamera) {
                // Small delay to ensure file is ready
                setTimeout(() => {
                    sendToOCR(file);
                }, 100);
            }
        }
        
        // Reset input so same file can be selected again
        event.target.value = '';
    };

    const handleCameraClick = async () => {
        try {
            // Request camera access
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { 
                    facingMode: 'environment', // Use back camera on mobile
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                },
                audio: false
            });
            
            // Open dialog first
            setCameraOpen(true);
            
            // Set stream after dialog is open
            setTimeout(() => {
                setStream(mediaStream);
            }, 50);
        } catch (error) {
            console.error("Error accessing camera:", error);
            let errorMsg = "Unable to access camera. ";
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                errorMsg += "Please allow camera permissions in your browser settings.";
            } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                errorMsg += "No camera found on this device.";
            } else {
                errorMsg += "Please check your camera or use file upload instead.";
            }
            setErrorMessage(errorMsg);
            // Fallback to file input if camera access fails
            cameraInputRef.current?.click();
        }
    };

    // Set up video stream when dialog opens
    useEffect(() => {
        if (cameraOpen && stream) {
            // Small delay to ensure dialog is fully rendered
            const timer = setTimeout(() => {
                if (videoRef.current && stream) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play()
                        .then(() => {
                            console.log("Video playing successfully");
                        })
                        .catch(err => {
                            console.error("Error playing video:", err);
                        });
                }
            }, 100);

            return () => {
                clearTimeout(timer);
                if (videoRef.current) {
                    videoRef.current.srcObject = null;
                    videoRef.current.pause();
                }
            };
        }
    }, [cameraOpen, stream]);

    const handleCloseCamera = () => {
        // Stop all tracks
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        setCameraOpen(false);
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            // Set canvas dimensions to match video
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            // Draw video frame to canvas
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Convert canvas to blob
            canvas.toBlob((blob) => {
                if (blob) {
                    // Create a File object from the blob
                    const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
                        type: 'image/jpeg',
                        lastModified: Date.now()
                    });

                    setSelectedFile(file);
                    setOcrResult(null);
                    setErrorMessage("");

                    // Create preview
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        setFilePreview(reader.result);
                    };
                    reader.readAsDataURL(file);

                    // Close camera and stop stream
                    handleCloseCamera();

                    // Automatically send to OCR
                    setTimeout(() => {
                        sendToOCR(file);
                    }, 100);
                }
            }, 'image/jpeg', 0.95);
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const sendToOCR = async (fileParam) => {
        let fileToSend = fileParam || selectedFile;
        // Normalize to a File/Blob if something else slipped in
        const toFile = (input) => {
            if (!input) return null;
            if (input instanceof File) return input;
            if (input instanceof Blob) return new File([input], `upload-${Date.now()}`, { type: input.type || 'application/octet-stream' });
            if (typeof input === 'string' && input.startsWith('data:')) {
                // Convert data URL to File
                const arr = input.split(',');
                const mime = arr[0].match(/:(.*?);/)[1];
                const bstr = atob(arr[1]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);
                while (n--) u8arr[n] = bstr.charCodeAt(n);
                return new File([u8arr], `upload-${Date.now()}`, { type: mime });
            }
            return null; // Unknown shape
        };

        if (!(fileToSend instanceof File) && !(fileToSend instanceof Blob)) {
            const normalized = toFile(fileToSend);
            if (normalized) fileToSend = normalized;
        }
        if (!fileToSend) {
            setErrorMessage("Please select a file before processing.");
            return;
        }

        setLoading(true);
        setErrorMessage("");
        setOcrResult(null);

        try {
            // Final guard: must be File or Blob at this point
            if (!(fileToSend instanceof File) && !(fileToSend instanceof Blob)) {
                console.error("Invalid file type passed to FormData.append", fileToSend);
                setErrorMessage("Invalid file object. Please re-upload and try again.");
                return;
            }

            const formData = new FormData();
            // FastAPI typically expects `files: List[UploadFile]`
            const filename = (fileToSend && (fileToSend.name || 'upload'));
            formData.append("files", fileToSend, filename);
            
            // Log FormData contents for debugging
            console.log("fileToSend diagnostics:", {
                isFile: fileToSend instanceof File,
                isBlob: fileToSend instanceof Blob,
                tag: Object.prototype.toString.call(fileToSend),
                name: fileToSend?.name,
                size: fileToSend?.size,
                type: fileToSend?.type,
            });
            console.log("FormData contents:");
            for (let [key, value] of formData.entries()) {
                if (value instanceof File) {
                    console.log(key, `${value.name} (${value.size} bytes)`, value.type);
                } else if (value instanceof Blob) {
                    console.log(key, `Blob (${value.size} bytes)`, value.type);
                } else {
                    console.log(key, typeof value + ':' + String(value));
                }
            }

            console.log("Sending file to OCR:", {
                fileName: fileToSend.name,
                fileSize: fileToSend.size,
                fileType: fileToSend.type,
                fieldName: "files",
                url: OCR_API_URL
            });

            const response = await axios.post(OCR_API_URL, formData, {
                // Let axios set Content-Type automatically to include boundary
                // Do not set Content-Type manually when using FormData
                timeout: 600000, // 10 minutes timeout for large PDFs (30+ pages)
            });

            console.log("OCR Response:", response);

            if (response.status === 200 || response.status === 201) {
                setOcrResult(response.data);
                setSummary(null); // Reset summary when new OCR result comes in
                setErrorMessage(""); // Clear any previous errors
            } else {
                setErrorMessage(`Failed to process OCR. Status: ${response.status}`);
            }
        } catch (error) {
            console.error("Error processing OCR:", error);
            
            let errorMsg = "An error occurred while processing the document. Please try again.";
            
            // Check for timeout errors
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                errorMsg = "Processing is taking longer than expected. Large documents (30+ pages) may take several minutes. The request is still processing on the server - please wait and try again, or try with a smaller document first.";
            } else if (error.response) {
                // Server responded with error status
                const status = error.response.status;
                const data = error.response.data;
                
                console.error("Error response:", {
                    status,
                    data,
                    headers: error.response.headers
                });
                
                // Log the full error data for debugging
                console.error("Full error data:", JSON.stringify(data, null, 2));
                
                // Try to extract meaningful error message
                if (data?.detail) {
                    // FastAPI often returns errors in 'detail' field
                    if (Array.isArray(data.detail)) {
                        errorMsg = data.detail.map(err => {
                            if (typeof err === 'string') return err;
                            return err.msg || err.loc?.join('.') + ': ' + (err.msg || JSON.stringify(err));
                        }).join("\n");
                    } else if (typeof data.detail === 'string') {
                        errorMsg = data.detail;
                    } else {
                        errorMsg = JSON.stringify(data.detail, null, 2);
                    }
                } else if (data?.message) {
                    errorMsg = data.message;
                } else if (data?.error) {
                    errorMsg = typeof data.error === 'string' ? data.error : JSON.stringify(data.error, null, 2);
                } else if (data?.msg) {
                    errorMsg = data.msg;
                } else if (typeof data === 'string') {
                    errorMsg = data;
                } else if (Object.keys(data).length > 0) {
                    // Show the error object structure
                    errorMsg = `Validation error (${status}):\n${JSON.stringify(data, null, 2)}`;
                } else {
                    errorMsg = `Server error (${status}): Unprocessable Entity - The file may be invalid or missing required fields.`;
                }
                
            } else if (error.request) {
                // Request was made but no response received
                console.error("No response received:", error.request);
                errorMsg = "No response from server. Please check your connection and try again.";
            } else {
                // Error setting up the request
                console.error("Error setting up request:", error.message);
                errorMsg = `Request error: ${error.message}`;
            }
            
            // Also check for CORS errors
            if (error.message && error.message.includes('CORS')) {
                errorMsg = "CORS error: The server may not allow requests from this origin.";
            }
            
            setErrorMessage(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const generateSummary = async () => {
        if (!ocrResult) {
            setErrorMessage("No OCR result available to summarize.");
            return;
        }

        const extractedText = extractTextFromOcr(ocrResult);

        if (!extractedText || !extractedText.trim()) {
            setErrorMessage("No text found in OCR result to summarize.");
            return;
        }

        setSummaryLoading(true);
        setErrorMessage("");

        try {
            const response = await axios.post(SUMMARIZE_API_URL, {
                ocr_text: extractedText
            }, {
                timeout: 120000, // 2 minutes timeout for summarization
            });

            const summaryText = response.data?.summary ?? response.data;
            if (response.status === 200 && summaryText) {
                setSummary(typeof summaryText === "string" ? summaryText : JSON.stringify(summaryText, null, 2));
            } else {
                setErrorMessage("Failed to generate summary. Please try again.");
            }
        } catch (error) {
            console.error("Error generating summary:", error, "URL:", SUMMARIZE_API_URL);
            let errorMsg = "An error occurred while generating the summary. Please try again.";

            if (error.response) {
                const data = error.response.data;
                if (data?.detail) {
                    errorMsg = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
                } else if (data?.message) {
                    errorMsg = data.message;
                }
            } else if (error.request) {
                errorMsg = `KnowledgeX backend not reachable at ${SUMMARIZE_API_URL}. Start KnowledgeX on port 8002 and restart npm start.`;
            } else {
                errorMsg = `Request error: ${error.message}`;
            }
            
            setErrorMessage(errorMsg);
        } finally {
            setSummaryLoading(false);
        }
    };

    const generatePDF = () => {
        if (!ocrResult) {
            setErrorMessage("No OCR result available to generate PDF.");
            return;
        }

        // Extract text from OCR result (same logic as renderOCRResult)
        let extractedText = "";
        let filename = "";
        
        if (ocrResult.results && Array.isArray(ocrResult.results) && ocrResult.results.length > 0) {
            const firstResult = ocrResult.results[0];
            extractedText = firstResult.text || "";
            filename = firstResult.filename || "extracted-document";
        } else if (typeof ocrResult === "string") {
            extractedText = ocrResult;
            filename = selectedFile?.name?.replace(/\.[^/.]+$/, "") || "extracted-document";
        } else if (ocrResult.readResult) {
            extractedText = ocrResult.readResult?.content || "";
            filename = selectedFile?.name?.replace(/\.[^/.]+$/, "") || "extracted-document";
        } else if (ocrResult.text) {
            extractedText = ocrResult.text;
            filename = selectedFile?.name?.replace(/\.[^/.]+$/, "") || "extracted-document";
        } else if (ocrResult.analyzeResult?.readResults) {
            const readResults = ocrResult.analyzeResult.readResults;
            extractedText = readResults
                .map((page) => page.lines?.map((line) => line.text).join("\n") || "")
                .join("\n\n");
            filename = selectedFile?.name?.replace(/\.[^/.]+$/, "") || "extracted-document";
        } else if (ocrResult.pages) {
            extractedText = ocrResult.pages
                .map((page) => page.lines?.map((line) => line.text).join("\n") || "")
                .join("\n\n");
            filename = selectedFile?.name?.replace(/\.[^/.]+$/, "") || "extracted-document";
        } else {
            extractedText = JSON.stringify(ocrResult, null, 2);
            filename = selectedFile?.name?.replace(/\.[^/.]+$/, "") || "extracted-document";
        }

        if (!extractedText || !extractedText.trim()) {
            setErrorMessage("No text found in OCR result to generate PDF.");
            return;
        }

        try {
            // Create new PDF document
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            // Set font
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(12);

            // Add title
            pdf.setFontSize(16);
            pdf.setFont("helvetica", "bold");
            pdf.text("Extracted Text Document", 105, 20, { align: 'center' });
            
            // Add metadata
            pdf.setFontSize(10);
            pdf.setFont("helvetica", "normal");
            const date = new Date().toLocaleDateString();
            pdf.text(`Generated on: ${date}`, 20, 30);
            if (filename) {
                pdf.text(`Source: ${filename}`, 20, 35);
            }

            // Split text into lines that fit the page width
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 20;
            const maxWidth = pageWidth - (margin * 2);
            const lineHeight = 7;
            let yPosition = 45; // Start below title and metadata

            // Split text by newlines and process each line
            const lines = extractedText.split('\n');
            
            lines.forEach((line) => {
                if (line.trim() === '') {
                    yPosition += lineHeight; // Add space for empty lines
                } else {
                    // Split long lines that exceed page width
                    const splitLines = pdf.splitTextToSize(line, maxWidth);
                    
                    splitLines.forEach((splitLine) => {
                        // Check if we need a new page
                        if (yPosition > pageHeight - margin) {
                            pdf.addPage();
                            yPosition = margin;
                        }
                        
                        pdf.text(splitLine, margin, yPosition);
                        yPosition += lineHeight;
                    });
                }
            });

            // Save the PDF
            const pdfFilename = `${filename}_extracted_${Date.now()}.pdf`;
            pdf.save(pdfFilename);
            
            setErrorMessage(""); // Clear any previous errors
        } catch (error) {
            console.error("Error generating PDF:", error);
            setErrorMessage("An error occurred while generating the PDF. Please try again.");
        }
    };

    const renderOCRResult = () => {
        if (!ocrResult) return null;

        // Handle different response formats
        let extractedText = "";
        let resultData = null;
        let filename = "";

        // New API format with results array
        if (ocrResult.results && Array.isArray(ocrResult.results) && ocrResult.results.length > 0) {
            resultData = ocrResult;
            const firstResult = ocrResult.results[0];
            extractedText = firstResult.text || "";
            filename = firstResult.filename || "";
        } else if (typeof ocrResult === "string") {
            extractedText = ocrResult;
        } else if (ocrResult.readResult) {
            // Azure Computer Vision Read API format
            resultData = ocrResult;
            extractedText = ocrResult.readResult?.content || "";
        } else if (ocrResult.text) {
            extractedText = ocrResult.text;
        } else if (ocrResult.analyzeResult?.readResults) {
            // Azure Form Recognizer format
            resultData = ocrResult;
            const readResults = ocrResult.analyzeResult.readResults;
            extractedText = readResults
                .map((page) => page.lines?.map((line) => line.text).join("\n") || "")
                .join("\n\n");
        } else if (ocrResult.pages) {
            // Alternative Azure format
            resultData = ocrResult;
            extractedText = ocrResult.pages
                .map((page) => page.lines?.map((line) => line.text).join("\n") || "")
                .join("\n\n");
        } else {
            // Try to extract text from any structure
            extractedText = JSON.stringify(ocrResult, null, 2);
        }

        // Format text as paragraph (replace multiple newlines with spaces, clean up formatting)
        const formatAsParagraph = (text) => {
            if (!text) return "";
            // Replace multiple newlines and line breaks with spaces
            let formatted = text
                .replace(/\n+/g, " ")  // Replace newlines with spaces
                .replace(/\s+/g, " ")  // Replace multiple spaces with single space
                .trim();               // Remove leading/trailing whitespace
            
            // Add proper sentence breaks (add space after periods if missing)
            formatted = formatted.replace(/\.([A-Za-z])/g, ". $1");
            
            return formatted;
        };

        const paragraphText = formatAsParagraph(extractedText);
        const rawDocumentText = extractedText;

        return (
            <Box sx={{ marginTop: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            color: "transparent", 
                            background: "linear-gradient(90deg, rgb(237, 30, 121) 0%, rgb(0, 113, 188) 100%)",  
                            backgroundClip: "text", 
                            fontFamily: "Poppins, sans-serif",
                            fontWeight: "bold",
                        }}
                    >
                        Extracted Text
                    </Typography>
                    {filename && (
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: "#94A3B8",
                                fontStyle: "italic",
                            }}
                        >
                            File: {filename}
                        </Typography>
                    )}
                </Box>
                
                <Paper 
                    sx={{ 
                        padding: 3, 
                        background: "#f5f5f5", 
                        borderRadius: 2,
                        maxHeight: "500px",
                        overflowY: "auto",
                        wordBreak: "break-word",
                    }}
                >
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            color: "#000",
                            fontFamily: "inherit",
                            fontSize: "16px",
                            lineHeight: "1.8",
                            textAlign: "left",
                            whiteSpace: "normal",
                        }}
                    >
                        {paragraphText || "No text extracted from the document."}
                    </Typography>
                </Paper>

                {/* Generate PDF and AI Summary Buttons */}
                <Box sx={{ marginTop: 2, display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
                    <Button
                        variant="contained"
                        onClick={generatePDF}
                        disabled={!extractedText || !extractedText.trim()}
                        startIcon={<PictureAsPdfIcon />}
                        sx={{
                            backgroundColor: "#d32f2f",
                            color: "white",
                            padding: "10px 30px",
                            borderRadius: "25px",
                            textTransform: "none",
                            fontSize: "16px",
                            fontWeight: "bold",
                            "&:hover": {
                                backgroundColor: "#b71c1c",
                            },
                            "&:disabled": {
                                backgroundColor: "#cccccc",
                                color: "#666666",
                            },
                        }}
                    >
                        Generate PDF
                    </Button>
                    <Button
                        variant="contained"
                        onClick={generateSummary}
                        disabled={summaryLoading || !extractedText || !extractedText.trim()}
                        startIcon={summaryLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        sx={{
                            background: "linear-gradient(90deg, rgb(237, 30, 121) 0%, rgb(0, 113, 188) 100%)",
                            color: "white",
                            padding: "10px 30px",
                            borderRadius: "25px",
                            textTransform: "none",
                            fontSize: "16px",
                            fontWeight: "bold",
                            "&:hover": {
                                background: "linear-gradient(90deg, rgb(200, 20, 100) 0%, rgb(0, 90, 150) 100%)",
                            },
                            "&:disabled": {
                                background: "#94A3B8",
                            }
                        }}
                    >
                        {summaryLoading ? "Generating Summary..." : "AI Summary"}
                    </Button>
                    <DocumentChat documentText={rawDocumentText} disabled={!rawDocumentText?.trim()} />
                </Box>

                {/* AI Summary Display */}
                {summary && (
                    <Box sx={{ marginTop: 3 }}>
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                color: "transparent", 
                                background: "linear-gradient(90deg, rgb(237, 30, 121) 0%, rgb(0, 113, 188) 100%)",  
                                backgroundClip: "text", 
                                fontFamily: "Poppins, sans-serif",
                                fontWeight: "bold",
                                marginBottom: 2,
                            }}
                        >
                            AI Summary
                        </Typography>
                        <Paper 
                            sx={{ 
                                padding: 3, 
                                background: "#1e293b", 
                                borderRadius: 2,
                                border: "1px solid #334155",
                            }}
                        >
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    color: "#e2e8f0",
                                    fontFamily: "inherit",
                                    fontSize: "16px",
                                    lineHeight: "1.8",
                                    textAlign: "left",
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                {summary}
                            </Typography>
                        </Paper>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Card sx={{ width: "100%", margin: "auto", padding: 5, backgroundColor: "#282c34", marginTop: 3 }}>
            <CardContent>
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            color: "transparent", 
                            background: "linear-gradient(90deg, rgb(237, 30, 121) 0%, rgb(0, 113, 188) 100%)",  
                            backgroundClip: "text", 
                            fontFamily: "Poppins, sans-serif",
                            fontWeight: "bold",
                            fontSize: "24px",
                            letterSpacing: "0.5px", 
                            textTransform: "uppercase"
                        }}
                    >
                        KnowledgeX - Document Intelligence
                    </Typography>
                </Box>

                {/* File Upload Section */}
                <Box sx={{ marginTop: 3 }}>
                    <Typography variant="h6" sx={{ color: "#94A3B8", marginBottom: 2 }}>
                        Upload Document
                    </Typography>
                    
                    {/* Hidden file inputs */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => handleFileSelect(e, false)}
                    />
                    <input
                        type="file"
                        ref={cameraInputRef}
                        style={{ display: "none" }}
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handleFileSelect(e, true)}
                    />

                    {/* Upload Options */}
                    <Box sx={{ display: "flex", gap: 2, marginTop: 2 }}>
                        <Button
                            variant="contained"
                            sx={{ 
                                bgcolor: "#7044ff", 
                                color: "white", 
                                "&:hover": { bgcolor: "#7044ff" },
                                flex: 1,
                            }}
                            startIcon={<UploadIcon />}
                            onClick={handleUploadClick}
                        >
                            Upload File
                        </Button>

                        <Button
                            variant="contained"
                            sx={{ 
                                bgcolor: "#EE3CFF", 
                                color: "white", 
                                "&:hover": { bgcolor: "#EE3CFF" },
                                flex: 1,
                            }}
                            startIcon={<CameraAltIcon />}
                            onClick={handleCameraClick}
                        >
                            Use Camera
                        </Button>
                    </Box>

                    {/* Selected File Info */}
                    {selectedFile && (
                        <Box sx={{ marginTop: 2 }}>
                            <Paper 
                                sx={{ 
                                    padding: 2, 
                                    background: "#1e1e1e", 
                                    borderRadius: 1,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Box>
                                    <Typography variant="body1" sx={{ color: "#94A3B8", fontWeight: "bold" }}>
                                        {selectedFile.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                                        {(selectedFile.size / 1024).toFixed(2)} KB
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    sx={{ color: "#94A3B8" }}
                                    onClick={() => {
                                        setSelectedFile(null);
                                        setFilePreview(null);
                                        setOcrResult(null);
                                        if (fileInputRef.current) fileInputRef.current.value = "";
                                        if (cameraInputRef.current) cameraInputRef.current.value = "";
                                    }}
                                >
                                    Remove
                                </Button>
                            </Paper>
                        </Box>
                    )}

                    {/* File Preview */}
                    {filePreview && (
                        <Box sx={{ marginTop: 2 }}>
                            <Typography variant="body2" sx={{ color: "#94A3B8", marginBottom: 1 }}>
                                Preview:
                            </Typography>
                            <Paper sx={{ padding: 1, background: "#1e1e1e", borderRadius: 1 }}>
                                <Box
                                    component="img"
                                    src={filePreview}
                                    alt="Preview"
                                    sx={{
                                        maxWidth: "100%",
                                        maxHeight: "300px",
                                        borderRadius: 1,
                                        objectFit: "contain",
                                    }}
                                />
                            </Paper>
                        </Box>
                    )}
                </Box>

                {/* Error Message */}
                {errorMessage && (
                    <Box sx={{ marginTop: 2 }}>
                        <Paper 
                            sx={{ 
                                padding: 2, 
                                background: "#3d1f1f", 
                                borderRadius: 1,
                                border: "1px solid #ff6b6b",
                            }}
                        >
                            <Typography 
                                variant="body2" 
                                color="error" 
                                sx={{ 
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    marginBottom: 1,
                                }}
                            >
                                Error:
                            </Typography>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: "#ff6b6b",
                                    textAlign: "left",
                                    wordBreak: "break-word",
                                    whiteSpace: "pre-wrap",
                                    fontFamily: "monospace",
                                    fontSize: "13px",
                                }}
                            >
                                {errorMessage}
                            </Typography>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: "#94A3B8",
                                    textAlign: "left",
                                    marginTop: 1,
                                    display: "block",
                                }}
                            >
                                Check the browser console (F12) for more details.
                            </Typography>
                        </Paper>
                    </Box>
                )}

                {/* Process Button */}
                <Box sx={{ display: "flex", gap: 2, marginTop: 3 }}>
                    <Button
                        variant="contained"
                        sx={{ 
                            backgroundColor: "#0a875a", 
                            color: "white", 
                            "&:hover": { backgroundColor: "#0a875a" },
                            flex: 1,
                        }}
                        startIcon={loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : <SendIcon />}
                        onClick={() => sendToOCR()}
                        disabled={loading || !selectedFile}
                    >
                        {loading ? "Processing..." : "Extract Text (OCR)"}
                    </Button>
                </Box>

                {/* OCR Results */}
                {renderOCRResult()}
            </CardContent>

            {/* Camera Dialog */}
            <Dialog
                open={cameraOpen}
                onClose={handleCloseCamera}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: "#282c34",
                        color: "white",
                    }
                }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6">Camera Capture</Typography>
                    <IconButton onClick={handleCloseCamera} sx={{ color: "white" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ position: "relative", width: "100%", backgroundColor: "#000", borderRadius: 2, overflow: "hidden", minHeight: "400px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {!stream && (
                            <Typography sx={{ color: "#94A3B8" }}>Loading camera...</Typography>
                        )}
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            style={{
                                width: "100%",
                                height: "auto",
                                display: stream ? "block" : "none",
                                maxHeight: "70vh",
                                objectFit: "contain",
                            }}
                            onLoadedMetadata={() => {
                                console.log("Video metadata loaded");
                            }}
                            onCanPlay={() => {
                                console.log("Video can play");
                            }}
                        />
                        <canvas
                            ref={canvasRef}
                            style={{ display: "none" }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: 2, justifyContent: "center" }}>
                    <Button
                        variant="contained"
                        onClick={capturePhoto}
                        startIcon={<PhotoCameraIcon />}
                        sx={{
                            backgroundColor: "#EE3CFF",
                            color: "white",
                            "&:hover": { backgroundColor: "#EE3CFF" },
                            padding: "12px 32px",
                            fontSize: "16px",
                        }}
                    >
                        Capture Photo
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default DocumentIntelligence;

